/** EMIS story lock — display only. Does not rename G1–G8, MDR, or nav. */

import { useLocation, useNavigate } from "react-router-dom";
import { navItemAllowed } from "./master";

export const PRODUCT_IDENTITY = "Enterprise Modernization Intelligence System";
export const APP_TITLE = "NextPromind | Full Stack Application";
export const PRODUCT_TAGLINE = "From “Why Modernize?” to “Value Proven.”";
export const PRODUCT_SUBTITLE =
  "Continuously understand enterprise reality, simulate modernization futures, support evidence-backed decisions, and prove realized value.";
export const PRODUCT_BOUNDARY =
  "YUG360-MIND understands, reasons, decides (advisory), assures and proves. It does not execute modernization.";
export const MASTER_JOURNEY = "WHY → DECIDE → DELIVER → PROVE";
export const BRD_OVERLAY_JOURNEY = MASTER_JOURNEY;
export const PRODUCT_PROBLEM =
  "Enterprise modernization decisions are fragmented across business, architecture, technology, cost, risk and execution systems — and the assumptions behind those decisions continuously change.";

export const STAKEHOLDERS = [
  { role: "CEO", focus: "Business value / investment" },
  { role: "CIO", focus: "Portfolio modernization" },
  { role: "CTO", focus: "Technology transformation" },
  { role: "Enterprise Architect", focus: "Standards / dependencies / target state" },
  { role: "Solution Architect", focus: "Application modernization decisions" },
  { role: "CISO", focus: "Security / risk / compliance" },
  { role: "CFO / FinOps", focus: "TCO / ROI / realized savings" },
  { role: "Program Manager", focus: "Transformation risk / milestones / dependencies" },
];

export const REQUIREMENT_GROUPS = [
  { id: "r1", label: "R1 Understand", note: "Now · eight domains + passport" },
  { id: "r2", label: "R2 Reason", note: "Now · drivers, unknowns, alternatives" },
  { id: "r3", label: "R3 Simulate", note: "Next · not in this workspace" },
  { id: "r4", label: "R4 Decide", note: "Now · 7R taxonomy + human MDR" },
  { id: "r5", label: "R5 Assure", note: "Lean · G1–G8, not drift engine" },
  { id: "r6", label: "R6 Learn", note: "Lean · G8 expected vs actual" },
];

export const LOGICAL_LAYERS = [
  "Experience",
  "Decision intelligence",
  "Enterprise state & memory",
  "Evidence fabric",
  "Enterprise systems",
];

export const STORY_VERBS = [
  { id: "why", label: "WHY", altitude: "now", note: "Business intent · driver · outcomes", route: "/assessment?focus=why" },
  { id: "assess", label: "ASSESS", altitude: "now", note: "Evidence · 8 domains · 7C/7S", route: "/assessment?focus=readiness" },
  { id: "decide", label: "DECIDE", altitude: "now", note: "7R hypotheses · human MDR", route: "/decision" },
  { id: "design", label: "DESIGN", altitude: "next", note: "Target architecture intent", route: "/enterprise?focus=strategy" },
  { id: "plan", label: "PLAN", altitude: "lean", note: "Wave order · not Jira", route: "/governance?focus=wave" },
  { id: "govern", label: "GOVERN", altitude: "now", note: "G1–G8 + immutable MDR", route: "/governance?focus=gates" },
  { id: "monitor", label: "MONITOR", altitude: "next", note: "Toolchain signals · Next", route: "/enterprise?focus=connectors" },
  { id: "prove", label: "PROVE", altitude: "now", note: "G8 expected vs actual", route: "/delivery?focus=value" },
];

/** Active spine verb from the URL so the highlight always matches navigation. */
export function spineCurrentFromLocation(pathname, search = "") {
  const params = new URLSearchParams(search);
  const step = params.get("step");
  const focus = params.get("focus");
  if (pathname === "/assessment") return step === "why" || focus === "why" ? "why" : "assess";
  if (pathname === "/evidence") return "assess";
  if (pathname === "/decision" || pathname === "/cockpit") return "decide";
  if (pathname === "/enterprise") return step === "monitor" || focus === "connectors" || focus === "monitor" ? "monitor" : "design";
  if (pathname === "/governance") return step === "plan" || focus === "wave" ? "plan" : "govern";
  if (pathname === "/delivery") return "prove";
  if (pathname === "/program") return "program";
  if (pathname === "/lifecycle") return "lifecycle";
  if (pathname === "/") return "portfolio";
  return null;
}

/** Ordered walk used by Back / Next. Existing routes without ?step= still resolve. */
export const STORY_FLOW = [
  { id: "portfolio", label: "Portfolio", route: "/" },
  { id: "program", label: "Program", route: "/program" },
  { id: "lifecycle", label: "Lifecycle", route: "/lifecycle" },
  ...STORY_VERBS,
];

export function spineNeighbors(pathname, search = "") {
  const currentId = spineCurrentFromLocation(pathname, search);
  const index = STORY_FLOW.findIndex((item) => item.id === currentId);
  if (index < 0) {
    return { prev: STORY_FLOW[STORY_FLOW.length - 1], next: STORY_FLOW[0] };
  }
  const last = STORY_FLOW.length - 1;
  return {
    prev: STORY_FLOW[index === 0 ? last : index - 1],
    next: STORY_FLOW[index === last ? 0 : index + 1],
  };
}

export function withApp(path, appId) {
  if (!path) return "/";
  const [pathname, query = ""] = String(path).split("?");
  const params = new URLSearchParams(query);
  if (appId) params.set("app", appId);
  params.delete("step");
  const encoded = params.toString();
  return encoded ? `${pathname}?${encoded}` : pathname;
}

export function spineNeighborsAllowed(pathname, search, guide) {
  const flow = STORY_FLOW.filter((item) => navItemAllowed(item.route, guide));
  const currentId = spineCurrentFromLocation(pathname, search);
  const index = flow.findIndex((item) => item.id === currentId);
  if (index < 0) {
    return { prev: flow[flow.length - 1] || null, next: flow[0] || null };
  }
  const last = flow.length - 1;
  return {
    prev: flow[index === 0 ? last : index - 1],
    next: flow[index === last ? 0 : index + 1],
  };
}

export function useAppGo(appId) {
  const navigate = useNavigate();
  const location = useLocation();
  const id = appId || new URLSearchParams(location.search).get("app");
  return (path) => navigate(withApp(path, id));
}

export const DRIVER_GROUPS = [
  { id: "business", label: "Business", domains: ["why"], examples: "Growth · Customer · Time-to-market" },
  { id: "financial", label: "Financial", domains: ["financial"], examples: "Cost · Licensing · Infrastructure" },
  { id: "technology", label: "Technology", domains: ["technology", "dependencies"], examples: "Obsolescence · Debt · Coupling" },
  { id: "risk", label: "Risk", domains: ["security", "enterprise"], examples: "Security · Compliance · Policy" },
  { id: "operational", label: "Operational", domains: ["operations", "delivery"], examples: "Incidents · Availability · Readiness" },
  { id: "strategic", label: "Strategic", domains: ["enterprise"], examples: "Cloud · Data · AI readiness" },
];

export const PASSPORT_PROFILE_KEYS = [
  "Identity",
  "Technology",
  "Hosting",
  "Dependencies",
  "Security",
  "Operations",
  "Data",
  "Cost baseline",
  "Evidence quality",
  "Last ingest",
];

export function profilePassport(passport = {}) {
  const hidden = /recommend|strateg|7r|confidence/i;
  return Object.entries(passport).filter(([key]) => !key.startsWith("_") && !hidden.test(key));
}

export function modernizationHealth({ authorized, waveReady, planningEligible, blockedGates }) {
  if (!authorized) {
    return { tone: "rose", label: "RED", detail: "No authorized MDR. Planning and GO stay closed." };
  }
  if (blockedGates > 0 || !waveReady || !planningEligible) {
    return { tone: "amber", label: "AMBER", detail: "Authorized, but wave readiness, owners or a gate still blocks modernization health. Not a slip probability." };
  }
  return { tone: "green", label: "GREEN", detail: "MDR sealed and wave-ready. Execution remains in the enterprise toolchain." };
}

/** Evidence-quality components. Never invent source-reliability or cost % without a source. */
export function confidenceBreakdown(app) {
  const fromApi = app?.decision_intelligence?.confidence_breakdown;
  if (fromApi?.components?.length) return fromApi;
  const readiness = app?.input_foundation?.readiness || {};
  const deps = app?.decision_intelligence?.dependencies || {};
  const total = Number(readiness.total_facts) || 0;
  const coverage = Number(readiness.coverage ?? app?.evidence?.coverage ?? 0);
  const unknown = Number(readiness.unknown || 0);
  const conflicting = Number(readiness.conflicting || 0);
  const stale = Number(readiness.stale || 0);
  const unknownOwners = Number(deps.unknown_owners || 0);
  const depTotal = Number(deps.total) || 0;
  const freshness = total <= 0 ? null : Math.max(0, Math.round(100 - (stale / total) * 100));
  const depCertainty = depTotal <= 0 ? null : Math.max(0, Math.round(100 - (unknownOwners / depTotal) * 100));
  const composite = total <= 0 ? null : Math.max(0, Math.min(100, Math.round(
    coverage * 0.50
    + (freshness || 0) * 0.20
    + (100 - (unknown / total) * 100) * 0.15
    + (100 - (conflicting / total) * 100) * 0.10
    + (depCertainty == null ? 100 : depCertainty) * 0.05
  )));
  const acquire = [];
  if (unknown) acquire.push("Resolve UNKNOWN facts that materially affect the decision");
  if (conflicting) acquire.push("Resolve CONFLICTING evidence");
  if (stale) acquire.push("Refresh STALE evidence");
  if (unknownOwners) acquire.push("Identify unknown dependency owners");
  return {
    headline: "Evidence quality — not authorization",
    composite,
    components: [
      { id: "coverage", label: "Evidence coverage", value: `${coverage}%` },
      { id: "freshness", label: "Evidence freshness", value: freshness == null ? "—" : `${freshness}%`, note: freshness == null ? "no facts" : `${stale} stale` },
      { id: "unknown", label: "Unknown facts", value: String(unknown) },
      { id: "conflict", label: "Conflicting facts", value: String(conflicting) },
      { id: "dependency", label: "Dependency certainty", value: depCertainty == null ? "—" : `${depCertainty}%`, note: depCertainty == null ? "none mapped" : `${unknownOwners} unknown owners` },
    ],
    acquire_next: acquire.length ? acquire : ["No material gaps in the current snapshot."],
  };
}
